import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LocalStorageService } from './connectors/localstorage';
import { UserBehaviorService } from './services/user-events/user-behavior.service';
import { AccountInfo } from '@azure/msal-browser';
import { menuItems } from './menu-items';

@Injectable({
  providedIn: 'root'
})
export class MicrosoftLoginGuard implements CanActivate {
  deviceInfo: any;

  constructor(
    private authService: MsalService,
    private userBehavior: UserBehaviorService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private localStorage: LocalStorageService
  ) {
    this.epicFunction();
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const loggedByMsal = this.authService.instance.getActiveAccount();

    if (loggedByMsal == null) {
      this.reset();
      this.router.navigate(['/login']);
      return false;
    }

    this.setMsalLoggedInUser(loggedByMsal);

    const rawRoles = this.localStorage.decryptAndGet('roles-access');
    const rawTeams = this.localStorage.decryptAndGet('teams-access');
    const roles: any[] = Array.isArray(rawRoles) ? rawRoles : [];
    const teams: any[] = Array.isArray(rawTeams) ? rawTeams : [];

    // 1. Validação de Bloqueio (Blacklist)
    if (route.data['denied'] != undefined && route.data['denied'] != null) {
      const hasDeniedRole = roles.some((role: any) => route.data['denied'].includes(role));
      const hasDeniedTeam = teams.some((team: any) => route.data['denied'].includes(team));

      if (hasDeniedRole || hasDeniedTeam) {
        this.router.navigate(['/access-denied']);
        return false;
      }
    }

    // 2. Busca Dinâmica no JSON
    // Limpeza profunda: Remove tudo depois de '?' ou '#' e limpa as barras '/'
    const currentLink = state.url
      .split(/[?#]/)[0]
      .replace(/^\/+|\/+$/g, '')
      .trim();

    const requiredAccess = this.findRequiredAccess(menuItems, currentLink);

    if (requiredAccess && requiredAccess.length > 0) {
      const hasAccess =
        roles.some((role: any) => requiredAccess.includes(role)) ||
        teams.some((team: any) => requiredAccess.includes(team));

      if (!hasAccess) {
        this.router.navigate(['/access-denied']);
        return false;
      }

      return true;
    }

    // 3. Fallback (Rotas legadas)
    if (route.data['required'] != undefined && route.data['required'] != null) {
      const hasAccess =
        roles.some((role: any) => route.data['required'].includes(role)) ||
        teams.some((team: any) => route.data['required'].includes(team));

      if (!hasAccess) {
        this.router.navigate(['/access-denied']);
        return false;
      }
    }

    return true;
  }

  private findRequiredAccess(items: any, targetLink: string): string[] | null {
    if (!items) return null;

    let safeItems = items;
    if (!Array.isArray(items) && items.menuItems) {
      safeItems = items.menuItems;
    } else if (!Array.isArray(items) && items.default) {
      safeItems = items.default;
    }

    const menuArray = Array.isArray(safeItems) ? safeItems : [safeItems];

    for (const item of menuArray) {
      if (Array.isArray(item)) {
        const foundInArray = this.findRequiredAccess(item, targetLink);
        if (foundInArray) return foundInArray;
        continue;
      }

      // Prevenção extra: só processa se o link for uma string não vazia
      if (typeof item.link === 'string' && item.link.trim() !== '') {
        const cleanItemLink = item.link.replace(/^\/+|\/+$/g, '').trim();

        if (cleanItemLink === targetLink && Array.isArray(item.required) && item.required.length > 0) {
          return item.required;
        }
      }

      // Busca recursiva nas propriedades "filhas"
      const possibleChildrenProps = ['items', 'child', 'menuShortcuts', 'areas', 'children'];

      for (const prop of possibleChildrenProps) {
        if (item[prop] && Array.isArray(item[prop]) && item[prop].length > 0) {
          const foundInChild = this.findRequiredAccess(item[prop], targetLink);
          if (foundInChild) {
            return foundInChild;
          }
        }
      }
    }

    return null;
  }

  reset(): void {
    this.userBehavior.setUser({
      name: '',
      email: '',
      from: ''
    });

    this.userBehavior.resetLocalStorageEvent();

    this.localStorage.remove('teams-access');
    this.localStorage.remove('roles-access');
    this.localStorage.remove('profile');
    this.localStorage.remove('userId');
  }

  setMsalLoggedInUser(user: AccountInfo): void {
    if (!user) {
      return;
    }

    this.userBehavior.setUser({
      name: user.name,
      email: user.username,
      from: 'msal'
    });
  }

  epicFunction() {
    this.deviceInfo = this.deviceService.getDeviceInfo();
  }
}
